import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MailOpen } from "lucide-react";
import { SUBVERTICALS, TRADES } from "../lib/content";
import { fadeUpAt } from "../lib/motion";
import WorkflowsGrid from "../components/WorkflowsGrid";
import LegalFirewall from "../components/LegalFirewall";
import FoundingCustomerSection from "../components/FoundingCustomerSection";
import FAQSection from "../components/FAQSection";

const TRADE_TO_FORM = {
  roofing: "roofing",
  solar: "solar",
  pest: "pest_control",
  landscape: "landscaping",
  pools: "pools",
  foundation: "foundation",
};

export default function SubverticalPage() {
  const { trade } = useParams();
  const data = SUBVERTICALS[trade];
  const otherTrades = React.useMemo(
    () => TRADES.filter((t) => t.slug !== trade),
    [trade]
  );
  if (!data) return <Navigate to="/" replace />;

  return (
    <main data-testid={`subvertical-page-${trade}`}>
      <SubHero data={data} trade={trade} />
      <SubStats data={data} />
      <WorkflowsGrid filterIds={data.workflows} />
      <SubIntegrations data={data} />
      <SubQuote data={data} />
      <LegalFirewall />
      <FoundingCustomerSection defaultTrade={TRADE_TO_FORM[trade]} />
      <FAQSection />

      <section className="bg-[#FAF7F2] py-20">
        <div className="max-w-7xl mx-auto px-6">
          <p className="kicker">Other trades</p>
          <div className="mt-6 flex flex-wrap gap-3">
            {otherTrades.map((t) => (
              <Link
                key={t.slug}
                to={`/trades/${t.slug}`}
                data-testid={`other-trade-${t.slug}`}
                className="btn btn-ghost text-[14px] py-2 px-4"
              >
                {t.label} <ArrowRight size={14} />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

function SubHero({ data, trade }) {
  return (
    <section
      data-testid="subvertical-hero"
      className="relative min-h-[90svh] flex flex-col overflow-hidden"
    >
      <img
        src={data.image}
        alt={`${data.label} jobsite at dawn`}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 hero-vignette" />
      <div className="grain" />
      <div className="relative z-10 flex-1 flex items-center">
        <div className="max-w-7xl mx-auto w-full px-6 pt-28 pb-20">
          <p className="kicker" style={{ color: "#E07B3A" }}>
            LeadRiver for {data.label}
          </p>
          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="h-display text-white mt-6 text-[clamp(2.4rem,6vw,5rem)] max-w-4xl"
          >
            {data.hero_h1}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.7 }}
            className="font-serif-body text-[#FAF7F2]/90 mt-7 max-w-2xl text-[clamp(1.05rem,1.5vw,1.3rem)] leading-relaxed"
          >
            {data.hero_sub}
          </motion.p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href="#founding"
              className="btn btn-primary"
              data-testid={`sub-hero-cta-${trade}`}
            >
              Claim founding spot <ArrowRight size={16} />
            </a>
            <Link
              to="/#brief"
              className="btn btn-ghost border-white/30 text-white hover:bg-white/10"
            >
              <MailOpen size={16} /> See the 6am brief
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function SubStats({ data }) {
  return (
    <section className="bg-[#FAF7F2] py-24">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-10">
        {data.stats.map((s, i) => (
          <motion.div
            key={s.label}
            {...fadeUpAt(i)}
            className="border-t border-[#E5E0D8] pt-6"
            data-testid={`sub-stat-${i}`}
          >
            <p className="font-sans-tight font-black tracking-tighter text-[clamp(2.4rem,4.2vw,3.4rem)] text-[#0B3D5C] leading-none">
              {s.value}
            </p>
            <p className="font-serif-body text-[#4A4A4A] mt-3 text-[1.02rem] leading-relaxed">
              {s.label}
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

function SubIntegrations({ data }) {
  return (
    <section className="bg-[#F3EFE6] py-24">
      <div className="max-w-7xl mx-auto px-6">
        <p className="kicker">Plays nice with your stack</p>
        <h2 className="h-section text-[clamp(1.7rem,3vw,2.4rem)] mt-4 max-w-2xl">
          Built for the {data.label.toLowerCase()} software you already pay for.
        </h2>
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {data.integrations.map((name) => (
            <div key={name} className="logo-cell">
              <span className="dot" />
              <span className="text-[#1A1A1A]">{name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SubQuote({ data }) {
  return (
    <section className="bg-[#FAF7F2] py-24">
      <div className="max-w-5xl mx-auto px-6">
        <p className="kicker">From the field</p>
        <blockquote className="mt-5 font-serif-body text-[clamp(1.4rem,2.6vw,2.1rem)] leading-tight text-[#1A1A1A] tracking-tight">
          &ldquo;{data.quote.body}&rdquo;
        </blockquote>
        <p className="font-sans-tight text-[#4A4A4A] mt-5 text-[14px]">
          {data.quote.who}
        </p>
      </div>
    </section>
  );
}
